import * as yup from "yup";
import validator from 'validator'
import { STTerminology } from "../constants/terminology.js";
import { DATE_INPUT_MAX, DATE_INPUT_MIN, isValidDateInputValue } from "./dateInput.util";

export const MAX_PACK_DESCRIPTION = 200;

/** What people see in a rich-text description: tags and entities removed, whitespace collapsed. The limit counts this. */
export const visibleDescriptionLength = (html) =>
	[
		...String(html ?? "")
			.replace(/<[^>]*>/g, " ")
			.replace(/&nbsp;/g, " ")
			.replace(/&amp;/g, "&")
			.replace(/&lt;/g, "<")
			.replace(/&gt;/g, ">")
			.replace(/&quot;/g, '"')
			.replace(/&#39;/g, "'")
			.replace(/\s+/g, " ")
			.trim(),
	].length;

/** Hunt and quest pack description: required, has a letter, and at most 200 visible characters (same as the app and API). */
export const packDescription = () =>
	yup
		.string()
		.test(
			"max-visible",
			`Description can be up to ${MAX_PACK_DESCRIPTION} characters`,
			(value) => visibleDescriptionLength(value) <= MAX_PACK_DESCRIPTION
		);

/** Require non-empty text that includes at least one letter (blocks emoji/number/special-only). */
export const meaningfulText = (label) =>
	yup
		.string()
		.trim()
		.required(`${label} is required`)
		.test(
			"has-letter",
			`${label} must include at least one letter`,
			(value) => typeof value === "string" && /[A-Za-z]/.test(value)
		);

const boundedDate = (label) =>
	yup
		.string()
		.required(`${label} is required`)
		.test(
			"bounded-date",
			`${label} must be a valid date between 1900 and 2100`,
			(value) => isValidDateInputValue(value, DATE_INPUT_MIN, DATE_INPUT_MAX)
		);

const adminEmail = yup
	.string()
	.required("Email is required")
	.test("trim", "Email cannot start or end with a space", (value) => typeof value === "string" && value === value.trim())
	.email("Enter a valid email")
	.max(254, "Enter a valid email");

export const adminPasswordRules = yup
	.string()
	.required("Password is required")
	.min(8, "Password must be at least 8 characters and include an uppercase letter, lowercase letter, number, and special character.")
	.max(128, "Password must be at least 8 characters and include an uppercase letter, lowercase letter, number, and special character.")
	.matches(/[A-Z]/, "Password must be at least 8 characters and include an uppercase letter, lowercase letter, number, and special character.")
	.matches(/[a-z]/, "Password must be at least 8 characters and include an uppercase letter, lowercase letter, number, and special character.")
	.matches(/[0-9]/, "Password must be at least 8 characters and include an uppercase letter, lowercase letter, number, and special character.")
	.matches(/[^A-Za-z0-9]/, "Password must be at least 8 characters and include an uppercase letter, lowercase letter, number, and special character.");

export const loginValidation = yup.object({
	email: adminEmail,
	password: yup.string().required("Password is required").max(128, "Password is too long"),
});

export const getUserSchema = (isAdd = false) =>
	yup.object({
		name: meaningfulText("Name"),
		email: yup
			.string()
			.trim()
			.email("Invalid email")
			.required("Email is required")
			.test(
				"is-email",
				"Invalid email",
				(value) => typeof value === "string" && validator.isEmail(value)
			),
		phone: yup
			.string()
			.trim()
			.required("Phone is required")
			.matches(/^[0-9+\-\s()]{7,20}$/, "Enter a valid phone number"),
		address: yup.string().trim().max(255, "Address is too long"),
		city: yup
			.string()
			.trim()
			.max(100, "City is too long")
			.test(
				"city-letter",
				"City must include at least one letter when provided",
				(value) => !value || /[A-Za-z]/.test(value)
			),
		pincode: yup
			.string()
			.trim()
			.matches(/^$|^[A-Za-z0-9\-\s]{3,12}$/, "Enter a valid pincode"),
		country: yup.string().trim(),
		state: yup.string().trim(),
		password: isAdd
			? yup
					.string()
					.required("Password is required")
					.min(8, "Password must be at least 8 characters")
			: yup.string().notRequired(),
		confirmpassword: isAdd
			? yup
					.string()
					.required("Confirm password is required")
					.oneOf([yup.ref("password")], "Passwords must match")
			: yup.string().notRequired(),
	});

export const userSchema = getUserSchema(false);

export const companySchema = yup.object({
	name: meaningfulText("Company name").max(100, "Company name must not exceed 100 characters"),
	alias: meaningfulText("Alias").max(50, "Alias must not exceed 50 characters"),
	website: yup
		.string()
		.trim()
		.nullable()
		.notRequired()
		.test(
			"is-url",
			"Please enter a valid website URL",
			(value) => {
				if (!value || typeof value !== "string" || value.trim() === "") return true;
				const trimmed = value.trim();
				// Reject emojis or non-ASCII characters
				if (/[^\x20-\x7E]/.test(trimmed)) return false;
				// Must match standard domain structure with at least a 2-character letter TLD (rejects IP addresses & numbers)
				const urlRegex = /^(https?:\/\/)?([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/i;
				if (!urlRegex.test(trimmed)) return false;
				return validator.isURL(trimmed, {
					protocols: ["http", "https"],
					require_protocol: false,
					require_tld: true,
				});
			}
		),
});

export const categorySchema = yup.object({
	name: meaningfulText("Collection name"),
	description: meaningfulText("Description"),
	logo: yup.string().trim().required("Icon is required"),
	banner: yup.string().trim().required("Button Image is required"),
	coverimage: yup.string().trim().required("Hero Image is required"),
});

export const subCategorySchema = yup.object({
	name: meaningfulText("Sub-category name"),
	description: meaningfulText("Description"),
	category_ids: yup
		.array()
		.of(yup.string().required())
		.min(1, "Collection is required"),
});

export const itemGroupDraftSchema = yup.object({
	name: meaningfulText("Package name"),
});

export const itemGroupSchema = yup.object({
	name: meaningfulText("Package name"),
	description: meaningfulText("Description"),
	categoryIds: yup
		.array()
		.of(yup.number())
		.min(1, "Please select at least one category"),
	subCategoryIds: yup
		.array()
		.of(yup.number())
		.min(1, "Please select at least one sub-category"),
	category: yup.string().notRequired(),
	subCategory: yup.string().notRequired(),
	logo: yup.string().required("Package logo required"),
	bannerImg: yup.string().required("Package banner image required"),
	iapimage: yup.string().required("IAP image is required"),
	price: yup
		.number()
		.typeError("Price must be a valid number")
		.required("Price is required")
		.min(0, "Price cannot be negative"),
	items: yup
		.array()
		.of(
			yup.object().shape({
				item_name: meaningfulText(`${STTerminology.challenge} name`),
				item_desc: yup.string().notRequired("Challenge description is optional"),
				item_img: yup
					.string()
					.trim()
					.required("Challenge image is required"),
			}),
		)
		.min(1, "At least one challenge is required"),
});

/** Mark all complete-mode fields touched so inline errors render after validateForm. */
export function buildItemGroupCompleteTouched(values) {
	return {
		name: true,
		description: true,
		categoryIds: true,
		subCategoryIds: true,
		price: true,
		logo: true,
		bannerImg: true,
		iapimage: true,
		items: (values?.items || []).map(() => ({
			item_name: true,
			item_desc: true,
			item_img: true,
		})),
	};
}

export function firstFormikErrorMessage(errors) {
	if (!errors || typeof errors !== "object") return null;
	for (const key of Object.keys(errors)) {
		const value = errors[key];
		if (typeof value === "string" && value.trim()) return value;
		if (Array.isArray(value)) {
			for (const entry of value) {
				if (typeof entry === "string" && entry.trim()) return entry;
				if (entry && typeof entry === "object") {
					const nested = firstFormikErrorMessage(entry);
					if (nested) return nested;
				}
			}
		}
		if (value && typeof value === "object") {
			const nested = firstFormikErrorMessage(value);
			if (nested) return nested;
		}
	}
	return null;
}

export const questPackSchema = yup.object({
  name: meaningfulText("Quest pack name"),
  category_ids: yup
    .array()
    .of(yup.string().required())
    .min(1, "Select at least one collection"),
  subcategory_ids: yup
    .array()
    .of(yup.string().required())
    .min(1, "Select at least one sub-category"),
  description: meaningfulText("Description").concat(packDescription()),
  imageurl: yup.string().required("Quest pack image is required"),
  price: yup
    .number()
    .typeError("Price must be a valid number")
    .required("Price is required")
    .min(0, "Price cannot be negative"),
  items: yup
    .array()
    .of(
      yup.object().shape({
        itemname: meaningfulText(`${STTerminology.challenge} name`),
        description: yup.string().notRequired(),
        imageurl: yup
          .string()
          .trim()
          .required("Challenge image is required"),
      })
    )
    .min(1, "At least one challenge is required"),
});

export function buildQuestPackCompleteTouched(values) {
  const items = Array.isArray(values?.items) ? values.items : [];
  return {
    name: true,
    category_ids: true,
    subcategory_ids: true,
    description: true,
    imageurl: true,
    price: true,
    items: items.map(() => ({
      itemname: true,
      description: true,
      imageurl: true,
    })),
  };
}

export const questPackDraftSchema = yup.object({
  name: meaningfulText("Quest pack name"),
  description: packDescription(),
});

export const huntPackSchema = yup.object({
  title: meaningfulText("Hunt pack name"),
  category_ids: yup
    .array()
    .of(yup.string().required())
    .min(1, "Select at least one collection"),
  subcategory_ids: yup
    .array()
    .of(yup.string().required())
    .min(1, "Select at least one sub-category"),
  description: meaningfulText("Description").concat(packDescription()),
  imageurl: yup.string().required("Hunt pack image is required"),
  price: yup
    .number()
    .typeError("Price must be a valid number")
    .required("Price is required")
    .min(0, "Price cannot be negative"),
  items: yup
    .array()
    .of(
      yup.object().shape({
        itemname: meaningfulText(`${STTerminology.challenge} name`),
        description: yup.string().notRequired(),
        imageurl: yup
          .string()
          .trim()
          .required("Challenge image is required"),
      })
    )
    .min(1, "At least one challenge is required"),
});

export function buildHuntPackCompleteTouched(values) {
  const items = Array.isArray(values?.items) ? values.items : [];
  return {
    title: true,
    category_ids: true,
    subcategory_ids: true,
    description: true,
    imageurl: true,
    price: true,
    items: items.map(() => ({
      itemname: true,
      description: true,
      imageurl: true,
    })),
  };
}

export const huntPackDraftSchema = yup.object({
  title: meaningfulText("Hunt pack name"),
  description: packDescription(),
});

const optionalPriceNumber = () =>
	yup
		.number()
		.transform((value, originalValue) =>
			originalValue === "" || originalValue === null || originalValue === undefined
				? undefined
				: value
		)
		.typeError("Price must be a valid number")
		.min(0, "Price cannot be negative");

export const discountSchema = yup.object({
	discount_name: meaningfulText("Discount Title"),
	discount_code: yup.string().trim().required("Discount Code is required"),
	amount: yup.number().typeError("Discount Value must be a number").required("Discount Value is required"),
	item_pack_ids: yup
		.array()
		.of(yup.string())
		.test(
			"at-least-one-pack",
			"Select at least one challenge",
			function (value) {
				const { hunt_pack_ids, quest_pack_ids } = this.parent;
				const itemCount = Array.isArray(value) ? value.length : 0;
				const huntCount = Array.isArray(hunt_pack_ids) ? hunt_pack_ids.length : 0;
				const questCount = Array.isArray(quest_pack_ids) ? quest_pack_ids.length : 0;
				return itemCount + huntCount + questCount > 0;
			}
		),
	hunt_pack_ids: yup.array().of(yup.string()),
	quest_pack_ids: yup.array().of(yup.string()),
	start_date: boundedDate("Start date").test(
		"before-end",
		"Start date must be on/before end date",
		function (value) {
			const { end_date } = this.parent;
			if (!value || !end_date) return true;
			return value <= end_date;
		}
	),
	end_date: boundedDate("End date").test(
		"after-start",
		"End date must be on/after start date",
		function (value) {
			const { start_date } = this.parent;
			if (!value || !start_date) return true;
			return value >= start_date;
		}
	),
	max_users: yup.number().required("Max Users is required").positive("Max Users must be positive"),
});

export const subscriptionPlanSchema = yup.object({
	name: meaningfulText("Plan name"),
	description: meaningfulText("Description"),
	plan_type: yup.string().oneOf(["monthly", "yearly"]).required("Plan type is required"),
	id: yup.mixed().notRequired(),
	price_monthly: yup.mixed().when(["plan_type", "id"], {
		is: (plan_type, id) => plan_type === "monthly" && Number(id) !== 1,
		then: () => optionalPriceNumber().required("Monthly price is required"),
		otherwise: () => yup.mixed().notRequired(),
	}),
	price_annually: yup.mixed().when(["plan_type", "id"], {
		is: (plan_type, id) => plan_type === "yearly" && Number(id) !== 1,
		then: () => optionalPriceNumber().required("Annual price is required"),
		otherwise: () => yup.mixed().notRequired(),
	}),
});

export const addOnSchema = yup.object({
	name: meaningfulText("Name"),
	description: meaningfulText("Description"),
	price: yup
		.number()
		.transform((value, originalValue) =>
			originalValue === "" || originalValue === null || originalValue === undefined
				? undefined
				: value
		)
		.typeError("Price must be a valid number")
		.required("Price is required")
		.min(0, "Price cannot be negative"),
	items: yup
		.number()
		.transform((value, originalValue) =>
			originalValue === "" || originalValue === null || originalValue === undefined
				? undefined
				: value
		)
		.typeError("Challenges must be a valid number")
		.required("Number of challenges is required")
		.min(0, "Challenges cannot be negative"),
	teams: yup
		.number()
		.transform((value, originalValue) =>
			originalValue === "" || originalValue === null || originalValue === undefined
				? undefined
				: value
		)
		.typeError("Teams must be a valid number")
		.required("Number of teams is required")
		.min(0, "Teams cannot be negative"),
});

export const roleSchema = yup.object({
	role: meaningfulText("Role name"),
	description: meaningfulText("Description"),
});
